import type { PricingPlan } from "@/features/courses/api";

/**
 * What a Learn track costs to buy outright (`LN-BUY01`, founder 2026-10-08).
 *
 * The track page used to print "From ৳1,999" from `fromPrice`, which reads as
 * several plans when Learn C has exactly one, and it offered no way to buy
 * from where the price stood. This is the one plan's price in both currencies
 * plus its id, so the page can say "one payment, yours for good" and link
 * straight to checkout with `&plan=`. The plan id matters beyond the link: the
 * checkout only prices a non-Bangladesh account in dollars when a plan is
 * selected (`CheckoutClient`, `GR-CRS01`).
 *
 * `null` means "keep the old line": no plans read, a free track, or more than
 * one plan, where "From" is true and the choice belongs on the course page.
 */
export type LearnTrackMoney = {
  /** What the learner pays. */
  amount: number;
  /** The struck-through list price, only when it is higher than `amount`. */
  list: number | null;
  /** Whole percent off `list`, 0 when there is no sale. */
  percentOff: number;
};

export type LearnTrackPrice = {
  planId: string;
  bdt: LearnTrackMoney;
  /** The card price for a buyer outside Bangladesh; `null` when the plan is not sold in dollars. */
  usd: LearnTrackMoney | null;
};

function money(list: number, sale: number | undefined): LearnTrackMoney {
  const onSale = sale !== undefined && sale < list;
  const amount = onSale ? sale : list;
  return {
    amount,
    list: onSale ? list : null,
    percentOff: onSale && list > 0 ? Math.round(((list - amount) / list) * 100) : 0,
  };
}

export function trackPriceFromPlans(plans: PricingPlan[] | null | undefined): LearnTrackPrice | null {
  if (!plans || plans.length !== 1) return null;
  const plan = plans[0];
  if (!plan._id || plan.price.isFree || plan.price.currency !== "BDT") return null;

  const bdt = money(plan.price.list, plan.price.sale);
  if (bdt.amount <= 0) return null;

  // The same pick as the checkout's `usdEffective`, so the two never disagree.
  const usd =
    typeof plan.priceUsd === "number" && plan.priceUsd > 0 ? money(plan.priceUsd, plan.salePriceUsd) : null;

  return { planId: plan._id, bdt, usd };
}

export function trackCheckoutHref(locale: string, courseSlug: string, planId: string): string {
  return `/${locale}/checkout?course=${encodeURIComponent(courseSlug)}&plan=${encodeURIComponent(planId)}`;
}
